/**
 * Template Viewer Component
 *
 * Displays full template content with metadata for preview before use.
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Alert,
  Spinner,
  Badge,
  Tabs
} from 'flowbite-react';
import {
  HiArrowLeft,
  HiDocumentAdd,
  HiShieldCheck,
  HiInformationCircle,
  HiCode
} from 'react-icons/hi';
import { Make_Authorized_API_Call } from '../../services/api/utils';
import { API_ENDPOINTS, buildEndpoint } from '../../services/api/endpoints';
// import ReactMarkdown from 'react-markdown'; // Commented out due to workspace protocol issues

interface TemplateDetail {
  Template_ID: string;
  Title: string;
  Category: string;
  Type: string;
  Compliance_Frameworks: string[];
  Source_File: string;
  Content: string;
  Variables: string[];
  Metadata: {
    Description: string;
    Version: string;
    Created_At: string;
    Processed_Date: string;
  };
  Review_Requirements: {
    frequency: string;
    stakeholders: string[];
  };
  Approval_Workflow: string[];
  Compliance_Mappings: Record<string, any>;
}

const TemplateViewer: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<TemplateDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplate = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const endpoint = buildEndpoint(API_ENDPOINTS.COMPLIANCE_TEMPLATES.GET, {
        id: templateId!
      });

      const result = await Make_Authorized_API_Call<TemplateDetail>(
        endpoint,
        'GET',
        undefined,
        { params: { Template_ID: templateId } }
      );

      if (result.success && result.data && 'Records' in result.data && Array.isArray(result.data.Records) && result.data.Records[0]) {
        setTemplate(result.data.Records[0]);
      } else {
        throw new Error(result.message || 'Failed to fetch template');
      }
    } catch (err: any) {
      console.error('Error fetching template:', err);
      setError(err.message || 'Failed to load template');
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    if (templateId) {
      fetchTemplate();
    }
  }, [templateId, fetchTemplate]);

  const handleUseTemplate = () => {
    navigate(`/documents/new?template=${templateId}`);
  };

  const getFrameworkColor = (framework: string): string => {
    const colors: Record<string, string> = {
      'SOC2': 'blue',
      'ISO27001': 'purple',
      'GDPR': 'green',
      'CCPA': 'yellow'
    };
    return colors[framework] || 'gray';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner size="xl" />
        <span className="ml-3">Loading template...</span>
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="p-6">
        <Alert color="failure">
          <span className="font-medium">Error:</span> {error || 'Template not found'}
        </Alert>
        <Button className="mt-4" onClick={() => navigate('/templates')}>
          Back to Templates
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <Button
          color="light"
          onClick={() => navigate('/templates')}
          className="mb-4"
        >
          <HiArrowLeft className="mr-2" />
          Back to Templates
        </Button>

        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h1 className="text-3xl font-bold mb-2">{template.Title}</h1>
            <p className="text-gray-600 mb-4">{template.Metadata.Description}</p>

            <div className="flex flex-wrap gap-2 mb-4">
              {template.Compliance_Frameworks.map(framework => (
                <Badge
                  key={framework}
                  color={getFrameworkColor(framework) as any}
                >
                  {framework}
                </Badge>
              ))}
              <Badge color="gray">
                {template.Category}
              </Badge>
            </div>
          </div>

          <Button onClick={handleUseTemplate}>
            <HiDocumentAdd className="mr-2" />
            Use This Template
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs>
        {/* Preview Tab */}
        <Tabs.Item title="Preview" icon={HiDocumentAdd}>
          <Card>
            <div className="prose max-w-none">
              <pre className="whitespace-pre-wrap">{template.Content}</pre>
            </div>
          </Card>
        </Tabs.Item>

        {/* Details Tab */}
        <Tabs.Item title="Details" icon={HiInformationCircle}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Metadata */}
            <Card>
              <h3 className="text-lg font-semibold mb-4">Template Information</h3>
              <dl className="space-y-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Template ID</dt>
                  <dd className="text-sm text-gray-900">{template.Template_ID}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Category</dt>
                  <dd className="text-sm text-gray-900 capitalize">{template.Category}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Type</dt>
                  <dd className="text-sm text-gray-900">{template.Type}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Version</dt>
                  <dd className="text-sm text-gray-900">{template.Metadata.Version}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Source</dt>
                  <dd className="text-sm text-gray-900">{template.Source_File}</dd>
                </div>
              </dl>
            </Card>

            {/* Review Requirements */}
            <Card>
              <h3 className="text-lg font-semibold mb-4">Review Requirements</h3>
              <dl className="space-y-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Review Frequency</dt>
                  <dd className="text-sm text-gray-900 capitalize">
                    {template.Review_Requirements.frequency}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Stakeholders</dt>
                  <dd className="text-sm text-gray-900">
                    <div className="flex flex-wrap gap-2 mt-1">
                      {template.Review_Requirements.stakeholders.map(stakeholder => (
                        <Badge key={stakeholder} color="blue">
                          {stakeholder}
                        </Badge>
                      ))}
                    </div>
                  </dd>
                </div>
              </dl>
            </Card>

            {/* Approval Workflow */}
            {template.Approval_Workflow.length > 0 && (
              <Card>
                <h3 className="text-lg font-semibold mb-4">Approval Workflow</h3>
                <ol className="space-y-2">
                  {template.Approval_Workflow.map((approver, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </span>
                      <span className="text-sm text-gray-900">{approver}</span>
                    </li>
                  ))}
                </ol>
              </Card>
            )}
          </div>
        </Tabs.Item>

        {/* Variables Tab */}
        <Tabs.Item title="Variables" icon={HiCode}>
          <Card>
            <h3 className="text-lg font-semibold mb-4">Template Variables</h3>
            <p className="text-sm text-gray-600 mb-4">
              These variables will need to be filled in when using this template:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {template.Variables.map(variable => (
                <Badge key={variable} color="gray" className="font-mono">
                  {'{{'}{variable}{'}}'}
                </Badge>
              ))}
            </div>
            {template.Variables.length === 0 && (
              <p className="text-sm text-gray-500">No variables in this template</p>
            )}
          </Card>
        </Tabs.Item>
      </Tabs>
    </div>
  );
};

export default TemplateViewer;
